import React from 'react';
import styles from './EmbedPreview.module.scss';

interface EmbedPreviewProps {
  html: string;
}

function isCalendlyEmbed(html: string): boolean {
  return /calendly/i.test(html);
}

function getAspectRatioPadding(html: string): { paddingTop?: string, cleanedHtml?: string } {
  // Skip aspect ratio logic for Calendly embeds
  if (isCalendlyEmbed(html)) {
    return { cleanedHtml: html };
  }

  let paddingTop: string | undefined;
  let currentHtml = html;

  // Rest of your existing logic...
  const divRegex = /<div[^>]*style=["'][^"']*(padding(?:-(?:top|bottom))?)\s*:\s*([^;"']+)[^"']*["'][^>]*>([\s\S]*?)<\/div>/i;

  while (true) {
    const match = currentHtml.match(divRegex);
    if (!match) break;

    const property = match[1];
    if (property === "padding-top" || property === "padding-bottom") {
      paddingTop = match[2].trim();
    } else {
      paddingTop = match[2].trim().split(/\s+/)[0];
    }

    const innerContent = match[3].trim();
    currentHtml = currentHtml.replace(divRegex, innerContent).trim();
  }

  if (paddingTop) {
    const numeric = parseFloat(paddingTop);
    if (!isNaN(numeric) && numeric < 56.25) {
      paddingTop = '56.25%';
    }
    return { paddingTop, cleanedHtml: currentHtml };
  }

  const iframeMatch = currentHtml.match(/<iframe[^>]*>/i);
  if (iframeMatch) {
    const iframeTag = iframeMatch[0];
    let width: number | undefined;
    let height: number | undefined;

    const widthAttr = iframeTag.match(/width\s*=\s*["']?(\d+)/i);
    const heightAttr = iframeTag.match(/height\s*=\s*["']?(\d+)/i);
    if (widthAttr) width = parseInt(widthAttr[1], 10);
    if (heightAttr) height = parseInt(heightAttr[1], 10);

    const styleMatch = iframeTag.match(/style=["']([^"']*)["']/i);
    if (styleMatch) {
      const style = styleMatch[1];
      const styleWidth = style.match(/width\s*:\s*(\d+)px/i);
      const styleHeight = style.match(/height\s*:\s*(\d+)px/i);
      if (!width && styleWidth) width = parseInt(styleWidth[1], 10);
      if (!height && styleHeight) height = parseInt(styleHeight[1], 10);
    }

    if (width && height) {
      let aspectRatio = (height / width) * 100;
      if (aspectRatio < 56.25) aspectRatio = 56.25;
      return { paddingTop: `${aspectRatio}%`, cleanedHtml: currentHtml };
    }
  }

  return { paddingTop: "56.25%" };
}

const EmbedPreview: React.FC<EmbedPreviewProps> = ({ html }) => {
  const containsScript = /<script[\s>]/i.test(html);
  const { paddingTop, cleanedHtml } = getAspectRatioPadding(html);

  // For Calendly embeds, use a flexible height container
  const isCalendly = isCalendlyEmbed(html);
  const style: React.CSSProperties = paddingTop && !isCalendly
    ? { paddingTop, position: 'relative' }
    : { position: 'relative', minHeight: isCalendly ? '700px' : 'auto' };

  if (containsScript) {
    const srcDoc = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            html, body {
              margin: 0;
              padding: 0;
              height: 100%;
              width: 100%;
              overflow: ${isCalendly ? 'auto' : 'hidden'};
            }

            iframe {
              margin: 0;
              padding: 0;
              border: 0;
              display: block;
              height: 100%;
              width: 100%;
            }
          </style>
        </head>
        <body>
          ${html}
        </body>
      </html>
    `;

    return (
      <div className={styles.embedContainer} style={style}>
        <iframe
          srcDoc={srcDoc}
          title="Embedded content"
          sandbox="allow-scripts allow-same-origin"
          frameBorder="0"
          style={{
            position: isCalendly ? 'static' : 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: isCalendly ? '700px' : '100%',
            border: 'none',
          }}
        />
      </div>
    );
  }

  return (
    <div
      className={styles.embedContainer}
      style={style}
      dangerouslySetInnerHTML={{ __html: cleanedHtml ? cleanedHtml : html }}
    />
  );
};

export default EmbedPreview;